import { HoverCard } from "@mantine/core";
import ReceiptLongTwoToneIcon from '@mui/icons-material/ReceiptLongTwoTone';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Stack, TextField } from "@mui/material";
import pdfMake from "pdfmake/build/pdfmake";
import { Content, TDocumentDefinitions } from "pdfmake/interfaces";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { URL_PREFIX } from "../../constants/stores";
import useScreenMode from "../../Hooks/useScreenMode";
import { getCorporateNo, getStoreInfo, VENDORS } from "../../Utils/utils";
interface VendorReportProps {
    storeList: any[];
    btnLabel?: string;
    account?: boolean;
}
const VendorReport: React.FC<VendorReportProps> = ({ btnLabel, storeList, account }) => {
    const [opened, setOpened] = useState(false);
    const token = useSelector((state: any) => state.user.sessionId);
    const user = useSelector((state: any) => state.user.user);
    const [date, setDate] = useState<{ start_dt: string; end_dt: string }>(() => {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        return {
            start_dt: thirtyDaysAgo.toISOString().split("T")[0],
            end_dt: today.toISOString().split("T")[0],
        };
    });
    const [corportateList, setCorporateList] = useState<any>([]);
    const { isMobileLike } = useScreenMode();
    const [filter, setFilter] = useState<{ payment_status: Boolean, approval: Boolean | null, invoice_status: Boolean, corporate_no: string | null, carrier: string | null }>({
        payment_status: false,
        approval: null,
        invoice_status: true,
        corporate_no: '',
        carrier: account ? 'G2000' : '',
    })
    useEffect(() => {
        const uniqueCorporateNos = Array.from(
            new Set(storeList.map((st: any) => st.corporate_no))
        );
        setCorporateList(uniqueCorporateNos);
    }, [storeList]);

    async function vendorReport(data: any, company: string, storeList: any[]) {
        const companyDetails = await VENDORS.find((vendor: any) => vendor.company === company);
        const filteredData = data.map((itm: any) => {
            // const receiverDetails = getStoreInfo('name', itm.receiver?.name, storeList);
            // const shipperDetails = getStoreInfo('name', itm.shipper?.name, storeList);
            // const corporateNo = getCorporateNo(shipperDetails, receiverDetails);
            const corporateNo = itm.corporation_no || '2138398 ONTARIO INC.';
            return filter.corporate_no ? corporateNo === filter.corporate_no ? { ...itm, corporate_no: corporateNo } : null : { ...itm, corporate_no: corporateNo };
        })
            .filter((itm: any) => itm !== null);

        try {
            const THEME_DARK = '#04162e';
            const THEME_MID = '#456491';
            const LIGHT_ROW = '#f8fafc';
            const SUMMARY_BG = '#eef3f8';
            const HST_RATE = 0.13;

            const formatDisplayDate = (value?: string | Date) => {
                if (!value) return 'N/A';
                const date = new Date(value);
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                const yyyy = date.getFullYear();
                return `${mm}/${dd}/${yyyy}`;
            };

            const getDueDate = (value?: string | Date) => {
                if (!value) return 'N/A';
                const date = new Date(value);
                date.setDate(date.getDate() + 30);
                return formatDisplayDate(date);
            };

            const getDueDays = (value?: string | Date) => {
                if (!value) return 'N/A';
                const dueDate = new Date(value);
                dueDate.setDate(dueDate.getDate() + 30);
                const today = new Date();
                const diffInMs = dueDate.getTime() - today.getTime();
                return `${Math.floor(diffInMs / (1000 * 60 * 60 * 24))}`;
            };

            const formatMoney = (amount?: number | string | null) => {
                return new Intl.NumberFormat('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(Number(amount || 0));
            };

            const headerCell = (text: string) => ({
                text,
                style: 'tableHeader',
                alignment: 'center' as const,
            });

            const bodyCell = (text: string | number, alignment: 'left' | 'center' | 'right' = 'left') => ({
                text: String(text ?? ''),
                style: 'tableBody',
                alignment,
            });

            const subTotal = filteredData.reduce(
                (total: number, item: any) => total + Number(item.amount || 0),
                0
            );
            const hstAmount = subTotal * HST_RATE;
            const totalAmount = subTotal + hstAmount;

            const reportDate = new Date().toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            });

            const tableBody = [
                [
                    headerCell('Customer'),
                    headerCell('Invoice'),
                    headerCell('#BOL'),
                    headerCell('Date'),
                    headerCell('Due Date'),
                    headerCell('Amount($)'),
                    headerCell('Due Days'),
                ],
                ...filteredData.map((item: any) => [
                    bodyCell(item?.corporate_no || 'N/A'),
                    bodyCell(item?.invoice_no || 'N/A'),
                    bodyCell(item?.bol_no || 'N/A'),
                    bodyCell(formatDisplayDate(item?.bol_date), 'center'),
                    bodyCell(getDueDate(item?.bol_date), 'center'),
                    bodyCell(formatMoney(Number(item?.amount || 0) * 1.13), 'right'),
                    bodyCell(getDueDays(item?.bol_date), 'center'),
                ]),
                [
                    {
                        text: 'Subtotal',
                        colSpan: 5,
                        alignment: 'right',
                        style: 'summaryLabel',
                    },
                    {},
                    {},
                    {},
                    {},
                    {
                        text: formatMoney(subTotal),
                        alignment: 'right',
                        style: 'summaryValue',
                    },
                    {},
                ],
                [
                    {
                        text: 'HST (13%)',
                        colSpan: 5,
                        alignment: 'right',
                        style: 'summaryLabel',
                    },
                    {},
                    {},
                    {},
                    {},
                    {
                        text: formatMoney(hstAmount),
                        alignment: 'right',
                        style: 'summaryValue',
                    },
                    {},
                ],
                [
                    {
                        text: 'Total',
                        colSpan: 5,
                        alignment: 'right',
                        style: 'summaryLabelBold',
                    },
                    {},
                    {},
                    {},
                    {},
                    {
                        text: formatMoney(totalAmount),
                        alignment: 'right',
                        style: 'summaryValueBold',
                    },
                    {},
                ],
            ];

            const contentData: any[] = [
                {
                    columns: [
                        {
                            // width: '*',
                            stack: [
                                {
                                    text: companyDetails?.name || 'N/A',
                                    style: 'reportBrand',
                                    alignment: 'left',
                                    margin: [0, 0, 0, 2],
                                },
                                {
                                    text: companyDetails?.address || '',
                                    style: 'metaText',
                                },
                                {
                                    text: companyDetails?.city || '',
                                    style: 'metaText',
                                },
                            ],
                        },
                        // {
                        //     stack: [
                        //         {
                        //             text: `Payment:${filter?.payment_status === undefined ? 'All' : filter.payment_status ? 'Paid' : 'Unpaid'} Approval:${filter.approval === null ? 'All' : filter.approval ? 'Approved' : 'Pending'}`,
                        //             style: 'reportTitle',
                        //             alignment: 'left',
                        //         },
                        //         {
                        //             text: `Invoice:${filter.invoice_status === undefined ? 'All' : filter.invoice_status ? 'Generated' : 'Not Generated'}\nCorporate No:${filter.corporate_no || 'All'}${account ? `\nCarrier:${filter.carrier || 'All'}` : ''}`,
                        //             style: 'reportTitle',
                        //             alignment: 'left',
                        //         },
                        //     ],
                        //     // width: 'auto',
                        // },
                        {
                            table: {
                                // widths: ['auto', '*'],
                                body: [
                                    [{ text: 'Corporation', bold: true }, { text: filter.corporate_no || 'All', colSpan: 3 }, {}, {}],
                                    [
                                        { text: 'Payment', bold: true },
                                        {
                                            text: filter?.payment_status === undefined
                                                ? 'All'
                                                : filter.payment_status
                                                    ? 'Paid'
                                                    : 'Unpaid'
                                        },
                                        { text: 'Approval', bold: true },
                                        {
                                            text: filter.approval === null
                                                ? 'All'
                                                : filter.approval
                                                    ? 'Approved'
                                                    : 'Pending'
                                        }
                                    ],
                                    [
                                        { text: 'Invoice', bold: true },
                                        {
                                            text: filter.invoice_status === undefined
                                                ? 'All'
                                                : filter.invoice_status
                                                    ? 'Generated'
                                                    : 'Not Generated'

                                            , colSpan: 3
                                        },
                                        {},
                                        {}
                                    ],                                    // ...(account
                                    //     ? [[
                                    //         { text: 'Carrier', bold: true },
                                    //         { text: filter.carrier || 'All' }
                                    //     ]]
                                    //     : []),
                                ],
                            },
                            layout: {
                                hLineWidth: () => 0.5,
                                vLineWidth: () => 0.5,
                                hLineColor: () => '#d1d5db',
                                vLineColor: () => '#d1d5db',
                                paddingLeft: () => 8,
                                paddingRight: () => 8,
                                paddingTop: () => 4,
                                paddingBottom: () => 4,
                            },
                            margin: [-25, 0, 0, 10],
                            alignment: 'left',
                        },
                        {
                            width: 'auto',
                            text: `Receivable Invoices As On\n${reportDate}`,
                            style: 'reportTitle',
                            alignment: 'right',
                        },
                    ],
                    margin: [0, 0, 0, 12],
                },
                {
                    table: {
                        headerRows: 1,
                        widths: [85, 45, 150, 50, 50, 70, 35],
                        body: tableBody,
                    },
                    layout: {
                        fillColor: (rowIndex: number) => {
                            if (rowIndex === 0) return THEME_DARK;
                            if (rowIndex >= filteredData.length + 1) return SUMMARY_BG;
                            return rowIndex % 2 === 0 ? LIGHT_ROW : null;
                        },
                        hLineColor: () => '#cfd8e3',
                        vLineColor: () => '#cfd8e3',
                        hLineWidth: () => 0.8,
                        vLineWidth: () => 0.8,
                        paddingLeft: () => 6,
                        paddingRight: () => 6,
                        paddingTop: () => 6,
                        paddingBottom: () => 6,
                    },
                },
            ];

            const docDefinition: TDocumentDefinitions = {
                content: contentData,
                pageSize: 'LETTER',
                pageMargins: [20, 20, 20, 20],
                pageOrientation: 'portrait',
                styles: {
                    reportBrand: {
                        fontSize: 14,
                        bold: true,
                        color: THEME_DARK,
                    },
                    reportTitle: {
                        fontSize: 12,
                        bold: true,
                        color: THEME_DARK,
                    },
                    metaText: {
                        fontSize: 10,
                        color: THEME_MID,
                    },
                    tableHeader: {
                        fontSize: 10,
                        bold: true,
                        color: 'white',
                    },
                    tableBody: {
                        fontSize: 9,
                        color: 'black',
                    },
                    summaryLabel: {
                        fontSize: 9,
                        bold: true,
                        color: THEME_DARK,
                    },
                    summaryValue: {
                        fontSize: 9,
                        bold: true,
                        color: THEME_DARK,
                    },
                    summaryLabelBold: {
                        fontSize: 10,
                        bold: true,
                        color: THEME_DARK,
                    },
                    summaryValueBold: {
                        fontSize: 10,
                        bold: true,
                        color: THEME_DARK,
                    },
                },
                defaultStyle: {
                    fontSize: 9,
                },
            };

            const pdfDoc = await pdfMake.createPdf(docDefinition);
            pdfDoc.open();
        } catch (error) {
            console.log('Error creating PDF', error);
        }
    }
    const handleViewReport = async () => {
        try {
            const response = await fetch(`${URL_PREFIX}/stats/vendorReport`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    start_date: date.start_dt,
                    end_date: date.end_dt,
                    // payment_status: filter.payment_status,
                    // approval: filter.approval,
                    // invoice_status: filter.invoice_status,
                    ...filter
                }),
            });
            if (response.status === 204) {
                alert('No data available.');
                return;
            }
            if (response.status === 200) {
                const { data } = await response.json();
                vendorReport(data, user.company, storeList);
            }
        } catch (error) {
            console.log('Error in report', error);
            alert('Error generating report.');
        }
    }
    const handleFilterChange = (column: string, value: string | boolean | undefined) => {
        setFilter({ ...filter, [column]: value });
    };

    return (
        <Box>
            <HoverCard width={200} shadow="md" withinPortal>
                <HoverCard.Target>
                    <ReceiptLongTwoToneIcon sx={{ cursor: 'pointer', '&:hover': { color: 'secondary.main' } }}
                        onClick={() => setOpened(true)}
                    />
                </HoverCard.Target>
                <HoverCard.Dropdown>
                    <Stack sx={{ p: 2 }}>
                        <span style={{ fontWeight: 'bold' }}>Download BOL Report</span>
                        <span>Click to download the Bill of Lading report as a PDF.</span>
                    </Stack>
                </HoverCard.Dropdown>
            </HoverCard>

            <Dialog
                open={opened}
                onClose={() => setOpened(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Report Filters</DialogTitle>

                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Start Date"
                                InputLabelProps={{ shrink: true }}
                                value={date.start_dt}
                                onChange={(e) =>
                                    setDate((prev: any) => ({ ...prev, start_dt: e.target.value }))
                                }
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                type="date"
                                label="End Date"
                                InputLabelProps={{ shrink: true }}
                                value={date.end_dt}
                                onChange={(e) =>
                                    setDate((prev: any) => ({ ...prev, end_dt: e.target.value }))
                                }
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                select
                                fullWidth
                                label="Payment"
                                value={filter.payment_status?.toString() ?? ''}
                                onChange={(e) =>
                                    handleFilterChange(
                                        'payment_status',
                                        e.target.value === '' ? undefined : e.target.value === 'true'
                                    )
                                }
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="false">Unpaid</MenuItem>
                                <MenuItem value="true">Paid</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                select
                                fullWidth
                                label="Approval"
                                value={
                                    filter.approval === undefined || filter.approval === null
                                        ? 'none'
                                        : filter.approval.toString()
                                }
                                onChange={(e) =>
                                    handleFilterChange(
                                        'approval',
                                        e.target.value === 'none'
                                            ? undefined
                                            : e.target.value === 'true'
                                    )
                                }
                            >
                                <MenuItem value="false">Pending</MenuItem>
                                <MenuItem value="true">Approved</MenuItem>
                                <MenuItem value="none">None</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                select
                                fullWidth
                                label="Invoice"
                                value={filter.invoice_status?.toString() ?? ''}
                                onChange={(e) =>
                                    handleFilterChange(
                                        'invoice_status',
                                        e.target.value === '' ? undefined : e.target.value === 'true'
                                    )
                                }
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="false">Not generated</MenuItem>
                                <MenuItem value="true">Generated</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                select
                                fullWidth
                                label="Corporate No"
                                value={filter.corporate_no?.toString() ?? ''}
                                onChange={(e) =>
                                    handleFilterChange('corporate_no', e.target.value || '')
                                }
                            >
                               <MenuItem value="">All</MenuItem>

{corportateList?.map((item: any) => {
    if (!item) return null;

    const value = typeof item === 'string' ? item : item.value;
    const label = typeof item === 'string' ? item : item.label;

    return (
        <MenuItem key={value} value={value}>
            {label}
        </MenuItem>
    );
})}
                            </TextField>
                        </Grid>

                        {account && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Carrier"
                                    value={filter.carrier || ''}
                                    onChange={(e) =>
                                        handleFilterChange('carrier', e.target.value || '')
                                    }
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="G2000">G2000</MenuItem>
                                    <MenuItem value="SVP SPORTS">SVP SPORTS</MenuItem>
                                    <MenuItem value="SMARTWAY">SMARTWAY</MenuItem>
                                </TextField>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button variant="outlined" onClick={() => setOpened(false)}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleViewReport}>
                        View
                    </Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
}

export default VendorReport;